import { useQuery } from '@tanstack/react-query';

import { api as apiPublicApi } from '_core/api/api-public-api';
import { useNavigationContext } from '_core/context/navigation-context';
import { getCompanyLogos } from '_core/utils/company-logos';
import { clientCompaniesData } from '_data/companies';
import { getOffsetAndLimitByPage } from '_features/longtail/components/Pagination/utils';
import { Agencies, CardsSectionOptions, Links, Page, Works } from '_types/public-api';

import { getPublicApiHost } from '../utils/public-links/utils';
import { getSortParam } from './public-api.ssr.repo';

export type Reviews = { reviewsCount: number; reviewsAverage: string };

// TO FIX: the api route doing this in the old public-app was broken and is now removed.
// For now, it's good enough to hardcode these values and change from time to time.
// In the future, we should reimplement correctly.
export const TRUSTPILOT_DATA = { reviewsCount: 516, reviewsAverage: '0.9' };

export const getPublicApiRequestLink = (origin: string, locale: string, pathname: string) => {
  const cleanPath = pathname.replace('/embed', '').split('?')[0];
  return `${getPublicApiHost(origin)}/${locale}/${encodeURIComponent(cleanPath)}`.replace(/^https?:\/\//, '');
};

export const getPublicApiRequestHomeLink = (origin: string, locale: string) => {
  return `${getPublicApiHost(origin)}/${locale}/${encodeURIComponent('/')}`.replace(/^https?:\/\//, '');
};

const WORKS_LIMIT = 3;

// JSON:API sparse fieldsets (https://jsonapi.org/format/#fetching-sparse-fieldsets): the explicit
// attribute set each surface renders, kept in one place so the contract with the public-api is
// reviewable per resource type rather than scattered across call sites.
export const PUBLIC_API_FIELDS = {
  linkPage: 'host,locale,path',
  homeAgencyCard: [
    'name',
    'slug',
    'logo',
    'reviews_rating_total',
    'reviews_count',
    'tagline',
    'description',
    'visibility_plan',
    'external_id',
  ].join(','),
  workAgency: 'name,logo',
} as const;

export class PublicApiRepo {
  public static publicApiUrl = '';
  public static publicApiNavBarUrl = '';

  getPage = async ({ page }: { page: string }): Promise<Page> => {
    const url = `/pages/${page}`;
    return apiPublicApi.get(url).then((data) => data.data);
  };

  getAgencies = async ({
    page,
    section,
    number,
    sort,
    limit,
    fieldsNeeded,
    cardsOptions,
    locale = 'en',
  }: {
    page: string;
    section: 'organic' | 'paid';
    number?: string;
    sort?: string;
    limit?: number;
    fieldsNeeded?: string; // comma separated fields that you want the api to return, if nothing provided all fields will be returned.
    cardsOptions?: CardsSectionOptions;
    locale?: string;
  }): Promise<Agencies> => {
    const { offset, limit: staticLimit } = getOffsetAndLimitByPage(parseInt(number ?? '1'), cardsOptions?.limit);

    const filters = `&filter[section][]=${section}&filter[section][]=manual`;

    const sortParam = getSortParam(sort, section);

    const fields = fieldsNeeded != null ? `&fields%5Bagency%5D=${fieldsNeeded}` : '';

    const url = `/pages/${page}/pages_agencies?page[offset]=${offset}&page[limit]=${
      limit ?? staticLimit
    }${sortParam}${fields}${filters}`;

    return apiPublicApi.get(url, { headers: { 'Accept-Language': locale } }).then((data) => data.data);
  };

  getLinks = async ({ page, queryParam }: { page: string; queryParam?: string }): Promise<Links> => {
    let composedQueryParams = `fields%5Bpage%5D=${PUBLIC_API_FIELDS.linkPage}`;
    if (queryParam) composedQueryParams = `${composedQueryParams}&${queryParam}`;

    const url = `/pages/${page}/links?${composedQueryParams}`;
    return apiPublicApi.get(url).then((data) => data.data);
  };

  getWorks = async ({ page, limit = WORKS_LIMIT }: { page: string; limit?: number }): Promise<Works> => {
    const url =
      `/pages/${page}/pages_works?include=work.agency` +
      `&fields%5Bagency%5D=${PUBLIC_API_FIELDS.workAgency}&page[limit]=${limit}`;
    return apiPublicApi.get(url).then((data) => data.data);
  };

  getReviews = async (): Promise<Reviews> => {
    return new Promise((resolve) => resolve(TRUSTPILOT_DATA));
  };
}

export const publicApiRepo = new PublicApiRepo();

export const cacheKey = {
  page: (url: string) => `page-${url}`,
  agencies: (url: string, section: 'organic' | 'paid') => `agencies-${url}-${section}`,
  links: (url: string) => `links-${url}`,
  works: (url: string) => `works-${url}`,
  reviews: 'reviews',
};

export function useGetPage(url: string) {
  return useQuery({
    queryKey: [cacheKey.page(url)],
    queryFn: () => {
      return publicApiRepo.getPage({ page: url });
    },
    enabled: false,
  });
}

export function useGetAgencies(url: string, section: 'organic' | 'paid') {
  const { query } = useNavigationContext();
  const { sort, position = '1' } = query;

  return useQuery({
    queryKey: [cacheKey.agencies(url, section)],
    queryFn: () => {
      return publicApiRepo.getAgencies({ page: url, section, sort: sort as string, number: position as string });
    },
    enabled: false,
  });
}

export function useGetLinks(url: string) {
  return useQuery({
    queryKey: [cacheKey.links(url)],
    queryFn: () => {
      return publicApiRepo.getLinks({ page: url });
    },
    enabled: false,
  });
}

export function useGetWorks(url: string) {
  return useQuery({
    queryKey: [cacheKey.works(url)],
    queryFn: () => {
      return publicApiRepo.getWorks({ page: url });
    },
    enabled: false,
  });
}

export function useGetReviews() {
  return useQuery({
    queryKey: [cacheKey.reviews],
    queryFn: () => {
      return publicApiRepo.getReviews();
    },
    enabled: false,
  });
}

export function useCompanies() {
  return useQuery({ queryKey: ['companies'], queryFn: () => getCompanyLogos(clientCompaniesData, 8) });
}
